<script setup lang="ts">
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    type?: string;
    placeholder?: string;
    isError?: boolean;
    disabled?: boolean;
    autocomplete?: string;
    numberOnly?: boolean;
    required?: boolean;
    minlength?: number | string;
    pattern?: string;
    min?: number;
    step?: number;
  }>(),
  {
    type: 'text',
    isError: false,
    disabled: false,
    required: false,
    step: 1,
  },
);

const inputRef = ref<HTMLInputElement | undefined>(undefined);
defineExpose({ input: inputRef });

const placeHolderText = computed(() => props.placeholder || t('Start_typing'));

const text = defineModel<string | number>('modelValue', { default: '' });
const textString = computed({
  get: () => {
    return String(text.value);
  },
  set: (val: string | number | undefined) => {
    text.value = String(val);
  },
},
);

const isEmpty = computed(() => !textString.value.length);

const isFocused = ref(false);

const borderClass = computed(() => {
  if (props.disabled) {
    return 'border-gray-300';
  }
  else if (props.isError) {
    return 'border-red-500';
  }
  else if (isFocused.value) {
    return 'border-blue-700';
  }
  else if (isEmpty.value) {
    return 'border-gray-300';
  }
  else {
    return 'border-gray-950';
  }
});
const slots = useSlots();
const isSlotEmpty = computed(() => !slots.default || slots.default().length === 0);
</script>

<template>
  <div class="relative">
    <div class="empty:hidden  absolute top-3 left-4">
      <slot />
    </div>
    <input
      :min="min"
      :step="step"
      ref="inputRef"
      v-model="textString"
      :minlength="minlength"
      :disabled="disabled"
      :type="type"
      :required="required"
      :pattern="pattern"
      :class="{
        'bg-gray-100': !disabled,
        'bg-gray-300': disabled,
        'text-gray-800': isEmpty,
        'text-gray-900': !isEmpty,
        [borderClass]: true,
      }"
      :autocomplete="autocomplete"
      class="w-full text-sub font-normal border rounded-normal py-2.5 px-4 outline-none text-gray-900 placeholder:text-gray-800 disabled:text-gray-800/50 placeholder:disabled:text-gray-800/50 empty:bg-gray-300 empty:focus:bg-gray-100 focus:bg-gray-100"
      :style="{
        'padding-left': isSlotEmpty ? '16px' : '40px',
      }"
      :placeholder="placeHolderText"
      @focusin="isFocused = true"
      @focusout="isFocused = false"
    >
  </div>
</template>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>
